import * as React from "react";
import { Button, Checkbox, FormControlLabel, Typography } from "@material-ui/core";
import { FormattedHTMLMessage } from "react-intl";
import { observer } from "mobx-react";
import styled from "styled-components";
import { API } from "../../../network/API";
import { authStore } from "../../../stores/AuthStore";
import { generalStore } from "../../../stores/GeneralStore";
import { t } from "../../../i18n/util";
import { history } from "../../routers/history";
import { Routes } from "../../routers/Routes";
import { UserNavigation } from "../../ui/UserNavigation";
import { Colors } from "../../util/Colors";

const SECONDARY_TEXT = "rgba(0, 0, 0, 0.54)";

const Container = styled.div`
    max-width: 900px;
    margin: 0 auto;
    padding: 40px;
    padding-bottom: 64px;

    & h3 {
        margin-bottom: 24px;
    }

    & p {
        margin-bottom: 16px;
        color: ${SECONDARY_TEXT};
        font-size: 1rem;
        line-height: 1.5;
    }

    & a {
        color: ${Colors.primary};
    }
`;

const CheckboxList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 16px 0 24px;

    & .MuiFormControlLabel-root {
        align-items: flex-start;
    }

    & .MuiFormControlLabel-root .MuiCheckbox-root {
        padding-top: 2px;
    }

    & .MuiFormControlLabel-label {
        color: ${SECONDARY_TEXT};
        font-size: 1rem;
        line-height: 1.5;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
`;

const Warning = styled(Typography)`
    && {
        color: ${Colors.danger};
        margin-top: 16px;
    }
`;

export const ConsentInformationSiteContent = observer(() => {
    const [informationRead, setInformationRead] = React.useState(false);
    const [termsAccepted, setTermsAccepted] = React.useState(false);
    const [confidentialityReleased, setConfidentialityReleased] = React.useState(false);
    const [showWarning, setShowWarning] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);

    // observe generalStore.locale so the page re-renders when the language switcher fires
    void generalStore.locale;

    const firstname = authStore.authProfile?.firstname ?? "";
    const lastname = authStore.authProfile?.lastname ?? "";
    const greeting =
        firstname && lastname ? t("consent.salutation", { firstname, lastname }) : t("consent.salutation.plain");

    const refreshScopeAndProfile = async () => {
        const { profile } = await API.getAuthProfile();
        authStore.authProfile = profile;
        await authStore.getUserProfile(profile.scope, profile.uid, profile.username);
    };

    const handleProceed = async () => {
        if (!(informationRead && termsAccepted && confidentialityReleased)) {
            setShowWarning(true);
            return;
        }

        setShowWarning(false);
        setSubmitting(true);
        try {
            await API.submitLegacyConsent();
            await refreshScopeAndProfile();
            history.push(Routes.PATIENT.ROOT);
        } catch (error) {
            generalStore.errorMessage = t("common.unexpectedError");
            if (error?.statusCode === 403) {
                await refreshScopeAndProfile();
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container>
            <h3>{greeting}</h3>
            <p>{t("consent.intro.firstParagraph")}</p>
            <p>{t("consent.intro.secondParagraph")}</p>
            <h3>{t("consent.intro.confirmHeader")}</h3>

            <CheckboxList>
                <FormControlLabel
                    control={
                        <Checkbox
                            color="primary"
                            checked={informationRead}
                            onChange={(_, checked) => setInformationRead(checked)}
                            inputProps={{ "aria-labelledby": "consent-checkbox-informationRead" }}
                        />
                    }
                    label={
                        <span id="consent-checkbox-informationRead">
                            <FormattedHTMLMessage id="consent.checkbox.informationRead" />
                        </span>
                    }
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            color="primary"
                            checked={termsAccepted}
                            onChange={(_, checked) => setTermsAccepted(checked)}
                            inputProps={{ "aria-labelledby": "consent-checkbox-termsAccepted" }}
                        />
                    }
                    label={
                        <span id="consent-checkbox-termsAccepted">
                            <FormattedHTMLMessage id="consent.checkbox.termsAccepted" />
                        </span>
                    }
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            color="primary"
                            checked={confidentialityReleased}
                            onChange={(_, checked) => setConfidentialityReleased(checked)}
                            inputProps={{ "aria-labelledby": "consent-checkbox-confidentialityReleased" }}
                        />
                    }
                    label={
                        <span id="consent-checkbox-confidentialityReleased">
                            {t("consent.checkbox.confidentialityReleased")}
                        </span>
                    }
                />
            </CheckboxList>

            <p>{t("consent.outro.warning")}</p>

            <p>{t("consent.privacyPolicyHeader")}</p>
            <p>
                <FormattedHTMLMessage id="consent.privacyPolicyBody" />
            </p>

            {showWarning && <Warning>{t("consent.warning.missingBoxes")}</Warning>}

            <ButtonContainer>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleProceed}
                    disabled={submitting}
                    aria-label={t("consent.button.proceedAriaLabel")}
                >
                    {t("consent.button.proceed")}
                </Button>
            </ButtonContainer>
        </Container>
    );
});

export function ConsentInformationSite() {
    return (
        <>
            <UserNavigation leftComponent={null} />
            <ConsentInformationSiteContent />
        </>
    );
}
